import { useState, useCallback, useEffect } from 'preact/hooks';

type ValidationRule<T> = {
  [K in keyof T]: (value: T[K]) => string;
};

type UseFormProps<T> = {
  initialValues: T;
  validationRules: ValidationRule<T>;
};

type FormReturn<T> = {
  formValues: T;
  errors: Partial<Record<keyof T, string>>;
  isValid: boolean;
  validateForm: () => boolean;
  register: (name: keyof T) => {
    name: keyof T;
    value: T[keyof T];
    onChange: (
      event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
    ) => void;
    onBlur: (event: FocusEvent) => void;
  };
};

export const useForm = <T extends Record<string, unknown>>({
  initialValues,
  validationRules,
}: UseFormProps<T>): FormReturn<T> => {
  /* useCallback is used to memoize functions (validateField, handleChange, handleBlur)
   so that their references don't change between renders. This is especially
   important for the functions that will be passed as props to form fields, preventing
   unnecessary re-renders. */

  const [formValues, setFormValues] = useState(initialValues);
  const [errors, setErrors] = useState<Partial<Record<keyof T, string>>>({});
  const [isValid, setIsValid] = useState(false);

  // Validates a single field based on validation rules
  const validateField = useCallback(
    (name: keyof T, value: T[keyof T]) => validationRules[name]?.(value) || '',
    [validationRules],
  );

  // Validates all fields and updates error state
  const validateForm = useCallback(() => {
    const formKeys = Object.keys(formValues) as Array<keyof T>;
    const newErrors: Partial<Record<keyof T, string>> = {};

    formKeys.forEach((field) => {
      const error = validateField(field, formValues[field]);
      if (error) {
        newErrors[field] = error;
      }
    });

    setErrors(newErrors);

    const isFormValid = formKeys.every((field) => !newErrors[field]);

    setIsValid(isFormValid);

    return isFormValid;
  }, [formValues, validateField]);

  // Helper to update error state for a field
  const updateErrorState = useCallback(
    (name: keyof T, value: T[keyof T]) => {
      const error = validateField(name, value);

      setErrors((prevErrors) => ({
        ...prevErrors,
        [name]: error,
      }));
    },
    [validateField],
  );

  // onChange updates form values and re-validates
  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
      const { name, value } = event.currentTarget;

      setFormValues((prev) => ({ ...prev, [name]: value }));

      updateErrorState(name as keyof T, value as T[keyof T]);
    },
    [updateErrorState],
  );

  // onBlur marks a field as touched and validates it
  const handleBlur = useCallback(
    (event: FocusEvent) => {
      const { name, value } = event.target as HTMLInputElement;

      updateErrorState(name as keyof T, value as T[keyof T]);
    },
    [updateErrorState],
  );

  // Checks overall validity whenever form values change
  useEffect(() => {
    const formKeys = Object.keys(formValues) as Array<keyof T>;

    const allValid = formKeys.every(
      (field) => !validateField(field, formValues[field]),
    );

    setIsValid(allValid);
  }, [formValues, validateField]);

  // Registers a field by providing its name, value, and handlers for onChange and onBlur
  const register = (name: keyof T) => ({
    name,
    value: formValues[name],
    onChange: handleChange,
    onBlur: handleBlur,
  });

  return {
    formValues,
    errors,
    isValid,
    validateForm,
    register,
  };
};
